import { useInfiniteQuery } from 'react-query';

import { Article, Briefing } from '@interfaces/homepage';
import { BookmarksContentResponse, getBookmarks } from '@utils/backend-api/bookmarks';

export const BOOKMARKS_LIST_KEY = 'BOOKMARKS_LIST';

type UseBookmarksListParams = {
  per_page: number;
  initialBookmarksContent?: (Article | Briefing)[];
  initialPage?: number;
  initialTotalPages?: number;
};

export default function useBookmarksList({
  per_page,
  initialBookmarksContent,
  initialPage = 1,
  initialTotalPages = 1,
}: UseBookmarksListParams) {
  const hasInitialData = initialBookmarksContent !== undefined;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<BookmarksContentResponse>(
      [BOOKMARKS_LIST_KEY, per_page],
      async ({ pageParam = 1 }) => await getBookmarks({ page: pageParam, per_page }),
      {
        initialData: hasInitialData
          ? {
              pages: [
                {
                  bookmarks_content: initialBookmarksContent,
                  page: initialPage,
                  totalPages: initialTotalPages,
                  total_pages: initialTotalPages,
                  hasNextPage: initialPage < initialTotalPages,
                  has_next_page: initialPage < initialTotalPages,
                  nextPage: initialPage < initialTotalPages ? initialPage + 1 : null,
                  next_page: initialPage < initialTotalPages ? initialPage + 1 : null,
                  per_page,
                  bookmarks: [],
                  total_count: 0,
                  totalCount: 0,
                  current_page: initialPage,
                  prev_page: null,
                  has_prev_page: false,
                },
              ],
              pageParams: [initialPage],
            }
          : undefined,
        getNextPageParam: (lastPage) =>
          lastPage.hasNextPage ? lastPage.nextPage ?? undefined : undefined,
      }
    );

  const bookmarks = data?.pages.flatMap((page) => page.bookmarks_content) ?? [];

  return {
    bookmarks,
    isLoadingBookmarks: isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}
